import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AcsButton, AcsCard, AcsInput, AcsLogo, AcsPageShell, AcsStatusBadge } from '../components';
import { useI18n } from '../i18n/I18nProvider';
import { api, type ContactRecord } from '../api/client';
import { ExportPanel } from './ExportPanel';

/**
 * Contatti raccolti e gestione dei diritti dell'interessato (§14).
 *
 * Lo stato di consenso mostrato qui è quello **derivato dal registro**, non un
 * flag: ciò che l'operatore vede coincide con ciò che finirebbe in un export.
 * Revoca e cancellazione richiedono una motivazione e finiscono in audit.
 */

type PendingAction =
  | { kind: 'withdraw'; contact: ContactRecord }
  | { kind: 'erase'; contact: ContactRecord }
  | null;

export function ContactsPage() {
  const { id = '' } = useParams();
  const { t, language } = useI18n();

  /**
   * Che cosa ha chiesto il visitatore, in ordine di precisione decrescente.
   *
   * L'etichetta della voce prima di tutto: più voci di una fiera mappano sullo
   * stesso `type` — è il caso normale, visto che una fiera declina la finalità
   * «documentazione» in più materiali — quindi ripiegare sul tipo renderebbe
   * indistinte richieste diverse.
   *
   * Se la voce è stata rimossa dalla configurazione resta il suo
   * identificatore: meno leggibile, ma pur sempre specifico. Il tipo generico è
   * l'ultima risorsa, ed è quella giusta per il ricontatto commerciale, che ha
   * una casella propria e nessun `resourceId`.
   */
  function describeRequest(request: {
    type: string;
    resourceId: string | null;
    itemLabels: Record<string, string> | null;
  }): string {
    const labels = request.itemLabels;
    if (labels) {
      const translated = labels[language] ?? Object.values(labels)[0];
      if (translated) return translated;
    }
    if (request.resourceId) return request.resourceId;
    return t.requestTypes[request.type as keyof typeof t.requestTypes] ?? request.type;
  }

  const queryClient = useQueryClient();

  const [pending, setPending] = useState<PendingAction>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [openHistory, setOpenHistory] = useState<string | null>(null);

  const contacts = useQuery({
    queryKey: ['contacts', id],
    queryFn: () => api.listContacts(id),
  });

  const done = (): void => {
    setPending(null);
    setReason('');
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['contacts', id] });
  };

  const withdraw = useMutation({
    mutationFn: (input: { contactId: string; reason: string }) =>
      api.withdrawConsent(input.contactId, 'EMAIL_MARKETING', input.reason),
    onSuccess: done,
    onError: (cause: Error) => setError(cause.message),
  });

  const erase = useMutation({
    mutationFn: (input: { contactId: string; reason: string }) =>
      api.eraseContact(input.contactId, input.reason),
    onSuccess: done,
    onError: (cause: Error) => setError(cause.message),
  });

  function confirmAction(): void {
    if (!pending || reason.trim().length === 0) return;
    const payload = { contactId: pending.contact.id, reason: reason.trim() };
    if (pending.kind === 'withdraw') withdraw.mutate(payload);
    else erase.mutate(payload);
  }

  return (
    <AcsPageShell header={<AcsLogo />}>
      <Link to={`/app/events/${id}`} style={{ color: 'var(--acs-color-primary)' }}>
        ← {t.app.back}
      </Link>

      <h1>{t.contacts.title}</h1>

      <div style={{ marginBottom: 'var(--acs-space-5)' }}>
        <ExportPanel eventId={id} />
      </div>

      {contacts.isLoading ? <p>{t.common.loading}</p> : null}
      {contacts.data?.contacts.length === 0 ? <p>{t.contacts.none}</p> : null}
      {error ? (
        <p className="acs-field__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="acs-stack">
        {contacts.data?.contacts.map((contact) => {
          const marketing = contact.consentStatuses.find(
            (status) => status.consentType === 'EMAIL_MARKETING',
          );

          return (
            <AcsCard key={contact.id}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 'var(--acs-space-4)',
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: 'var(--acs-font-size-lg)' }}>
                    {contact.anonymized
                      ? t.contacts.anonymized
                      : `${contact.firstName} ${contact.lastName}`}
                  </h2>
                  {!contact.anonymized ? (
                    <p style={{ margin: 0 }}>
                      {contact.email}
                      {contact.company ? ` · ${contact.company}` : ''}
                    </p>
                  ) : null}
                  <p style={{ margin: 0, color: 'var(--acs-color-text-muted)' }}>
                    {t.contacts.collectedAt} {new Date(contact.createdAt).toLocaleString()}
                  </p>
                  <p style={{ margin: 0, color: 'var(--acs-color-text-muted)' }}>
                    {/*
                      Etichette leggibili, non i codici interni: chi consulta
                      questa schermata risponde a una richiesta dell'interessato,
                      non legge un tracciato dati.
                    */}
                    {t.contacts.requests}:{' '}
                    {contact.requests.map(describeRequest).join(', ') || '—'}
                  </p>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <AcsStatusBadge>
                    {t.contacts.marketing}:{' '}
                    {marketing?.granted ? t.contacts.granted : t.contacts.notGranted}
                  </AcsStatusBadge>
                  {marketing?.granted && marketing.since ? (
                    <p style={{ margin: 'var(--acs-space-2) 0 0', color: 'var(--acs-color-text-muted)' }}>
                      {t.contacts.since} {new Date(marketing.since).toLocaleDateString()} ·{' '}
                      {marketing.consentTextVersion}
                    </p>
                  ) : null}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 'var(--acs-space-3)',
                  flexWrap: 'wrap',
                  marginTop: 'var(--acs-space-4)',
                }}
              >
                {contact.consentHistory.length > 0 ? (
                  <AcsButton
                    variant="secondary"
                    onClick={() => setOpenHistory(openHistory === contact.id ? null : contact.id)}
                  >
                    {openHistory === contact.id ? t.contacts.hideHistory : t.contacts.showHistory}
                  </AcsButton>
                ) : null}

                {marketing?.granted ? (
                  <AcsButton
                    variant="secondary"
                    onClick={() => {
                      setPending({ kind: 'withdraw', contact });
                      setReason('');
                    }}
                  >
                    {t.contacts.withdraw}
                  </AcsButton>
                ) : null}

                {!contact.anonymized ? (
                  <AcsButton
                    variant="secondary"
                    onClick={() => {
                      setPending({ kind: 'erase', contact });
                      setReason('');
                    }}
                  >
                    {t.contacts.erase}
                  </AcsButton>
                ) : null}
              </div>

              {openHistory === contact.id ? (
                <div style={{ marginTop: 'var(--acs-space-4)' }}>
                  <strong>{t.contacts.history}</strong>
                  <ul style={{ paddingLeft: '1.2rem' }}>
                    {contact.consentHistory.map((entry) => (
                      <li key={entry.id}>
                        <strong>{entry.action}</strong> · {new Date(entry.recordedAt).toLocaleString()}{' '}
                        · {entry.policyVersion}
                        {entry.consentTextVersion ? ` · ${entry.consentTextVersion}` : ''} ·{' '}
                        {entry.source}
                        {entry.reason ? ` — ${entry.reason}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </AcsCard>
          );
        })}
      </div>

      {pending ? (
        <div className="kiosk-dialog__backdrop" role="presentation">
          <div className="kiosk-dialog" role="dialog" aria-modal="true">
            <h2 style={{ marginTop: 0 }}>
              {pending.kind === 'withdraw'
                ? t.contacts.withdrawConfirmTitle
                : t.contacts.eraseConfirmTitle}
            </h2>
            {/* Spiega che cosa succede davvero, invece di un generico «sei sicuro?». */}
            <p style={{ color: 'var(--acs-color-text-muted)' }}>
              {pending.kind === 'withdraw' ? t.contacts.withdrawExplain : t.contacts.eraseExplain}
            </p>

            <AcsInput
              label={t.contacts.reasonLabel}
              placeholder={t.contacts.reasonPlaceholder}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoComplete="off"
            />

            <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
              <AcsButton
                disabled={reason.trim().length === 0 || withdraw.isPending || erase.isPending}
                onClick={confirmAction}
              >
                {t.contacts.confirm}
              </AcsButton>
              <AcsButton variant="secondary" onClick={() => setPending(null)}>
                {t.contacts.cancel}
              </AcsButton>
            </div>
          </div>
        </div>
      ) : null}
    </AcsPageShell>
  );
}
